import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { catchError, first, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { User } from "../models/auth.models";

export interface AgencyContextStore {
  id: number;
  name: string;
}

/** Cabeçalho que o servidor lê (`common/agency-context` no back-end). */
export const AGENCY_CONTEXT_HEADER = "X-Agency-Context";

/** Erro do servidor quando a agência pedida já não é válida. */
export const AGENCY_CONTEXT_INVALID = "t-agency-context-invalid";

const STORAGE_PREFIX = "agencyContext__";

/**
 * Contexto global de agência do MASTER (seletor no topbar).
 *
 * Guarda a agência escolhida e é a fonte que o `AgencyContextInterceptor` usa
 * para enviar o cabeçalho em cada pedido. Quem decide o âmbito é o servidor:
 * aqui não se filtra nada, e nada muda no papel ou na agência real do
 * utilizador.
 *
 * A aplicação começa SEMPRE em "Todas as agências": a escolha vale só
 * enquanto a aplicação está aberta (memória), não sobrevive à atualização do
 * navegador nem a um novo início de sessão.
 */
@Injectable({ providedIn: "root" })
export class AgencyContextService {
  private readonly storeSubject = new BehaviorSubject<AgencyContextStore | null>(null);
  readonly store$ = this.storeSubject.asObservable();

  /**
   * Emite a CADA troca. O interceptor cancela as leituras em curso, para que
   * uma resposta da agência anterior não chegue ao ecrã depois da troca.
   */
  private readonly changedSubject = new Subject<AgencyContextStore | null>();
  readonly changed$ = this.changedSubject.asObservable();

  /** Erro de validação do servidor, para o topbar avisar. */
  private readonly invalidSubject = new Subject<void>();
  readonly invalid$ = this.invalidSubject.asObservable();

  private owner: { userId: string; entityId: number } | null = null;
  private stores: AgencyContextStore[] | null = null;

  constructor(private readonly http: HttpClient) {}

  get store(): AgencyContextStore | null {
    return this.storeSubject.value;
  }

  get storeId(): number | null {
    return this.storeSubject.value?.id ?? null;
  }

  /** Só o MASTER real, fora do "Ver como", pode usar o contexto. */
  static isAvailableFor(user: User | null | undefined): boolean {
    return user?.role?.name === "MASTER" && !user?.impersonator;
  }

  /**
   * Chamado sempre que o utilizador é (re)conhecido, antes de as páginas
   * fazerem pedidos. O mesmo utilizador mantém a escolha atual; um utilizador
   * novo (ou a aplicação acabada de abrir) começa em "Todas as agências".
   * Para quem não pode usar o contexto, garante que não há nenhum.
   */
  restore(user: User | null | undefined): void {
    if (!AgencyContextService.isAvailableFor(user)) {
      this.owner = null;
      this.stores = null;
      this.apply(null);
      return;
    }

    const owner = { userId: `${user!.id}`, entityId: Number(user!.entity) };
    const sameOwner =
      this.owner?.userId === owner.userId && this.owner?.entityId === owner.entityId;
    this.owner = owner;
    if (sameOwner) return;

    this.stores = null;
    // Escolhas guardadas por versões anteriores deixam de ser repostas.
    this.forgetSaved();
    this.apply(null);
  }

  /** Agências da entidade para o seletor (pedido feito uma vez por sessão). */
  loadStores(): Observable<AgencyContextStore[]> {
    if (this.stores) return of(this.stores);

    return this.http
      .get<AgencyContextStore[]>(`${environment.apiUrl}/agency-context/stores`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        tap((stores) => (this.stores = stores ?? [])),
        catchError(() => of([] as AgencyContextStore[])),
      );
  }

  /** O detalhe de negócio aberto pertence ao contexto atual? */
  checkDeal(type: string, id: number): Observable<{ belongs: boolean }> {
    return this.http
      .get<{ belongs: boolean }>(
        `${environment.apiUrl}/agency-context/check/deal/${type}/${id}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Troca (ou remove, com `null`) a agência. */
  select(store: AgencyContextStore | null): void {
    this.apply(store);
  }

  /** Fim de sessão: esquece a escolha deste utilizador. */
  clear(): void {
    this.owner = null;
    this.stores = null;
    this.apply(null);
  }

  /** O servidor recusou a agência (apagada, ou de outra entidade). */
  handleInvalid(): void {
    if (!this.storeSubject.value) return;
    this.apply(null);
    this.invalidSubject.next();
  }

  private apply(store: AgencyContextStore | null): void {
    if ((store?.id ?? null) === this.storeId) return;
    this.storeSubject.next(store);
    this.changedSubject.next(store);
  }

  /** Remove a escolha que versões anteriores guardavam no localStorage. */
  private forgetSaved(): void {
    if (!this.owner) return;
    try {
      localStorage.removeItem(`${STORAGE_PREFIX}${this.owner.userId}__${this.owner.entityId}`);
    } catch {
      // Sem localStorage não há nada guardado.
    }
  }
}
